import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function LotTaxSettings() {
  const [lots, setLots] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/lots?includeInactive=true").then(({ data }) => {
      setLots(data);
      setDraft(Object.fromEntries(data.map((l) => [l.id, { tax: l.salesTaxPercent ?? "", live: l.isActive !== false, code: l.code || "" }])));
    });
  }, []);

  const save = async (lot) => {
    const d = draft[lot.id];
    setSaving(lot.id);
    try {
      const { data } = await api.put(`/lots/${lot.id}/config`, {
        salesTaxPercent: d.tax === "" ? null : Number(d.tax),
        isActive: d.live,
        ...(d.code ? { code: d.code } : {}),
      });
      setLots((ls) => ls.map((l) => (l.id === lot.id ? data : l)));
      toast.success(`${lot.name} saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const setD = (id, k, v) => setDraft((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  return (
    <Card data-testid="lot-tax-settings" className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
      <h2 className="font-heading text-xl font-bold text-slate-100">Lots, Codes &amp; Sales Tax</h2>
      <p className="mb-4 text-sm text-slate-400">
        Each lot charges its own county sales tax. Card and ACH are the same price: base × (1 + tax).
        Blank = not set (0% charged). Lot code prefixes ticket numbers (e.g. POW-250120-0147).
      </p>
      <div className="divide-y divide-slate-800">
        {lots.map((l) => (
          <div key={l.id} data-testid={`lot-config-row-${l.id}`} className="flex flex-wrap items-center gap-3 py-3">
            <div className="min-w-[220px] flex-1">
              <p className="font-semibold text-slate-100">{l.name}</p>
              <p className="text-xs text-slate-500">{l.address}</p>
            </div>
            <Input
              data-testid={`lot-code-input-${l.id}`}
              maxLength={5}
              placeholder="CODE"
              value={draft[l.id]?.code ?? ""}
              onChange={(e) => setD(l.id, "code", e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
              className="h-11 w-20 border-slate-700 bg-slate-950 font-data uppercase"
            />
            <div className="flex items-center gap-2">
              <Input
                data-testid={`lot-tax-input-${l.id}`}
                type="number"
                step="0.1"
                min="0"
                placeholder="TBD"
                value={draft[l.id]?.tax ?? ""}
                onChange={(e) => setD(l.id, "tax", e.target.value)}
                className="h-11 w-24 border-slate-700 bg-slate-950 font-data"
              />
              <span className="text-sm text-slate-400">%</span>
            </div>
            <label className="flex w-28 items-center gap-2 text-sm text-slate-300">
              <input
                data-testid={`lot-live-toggle-${l.id}`}
                type="checkbox"
                checked={!!draft[l.id]?.live}
                onChange={(e) => setD(l.id, "live", e.target.checked)}
                className="h-5 w-5 accent-[#A0148C]"
              />
              {draft[l.id]?.live ? "Live" : "Not live"}
            </label>
            <Button
              data-testid={`lot-config-save-${l.id}`}
              onClick={() => save(l)}
              disabled={saving === l.id}
              className="h-11 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]"
            >
              {saving === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
